/**
 * Guided bidding results - one document per signed-in user:
 *
 *   guidedBiddingResults/{uid}
 *     { uid, updatedAt,
 *       q: { "<setId>|<handId>|<stepId>": {
 *              seen, firstTry, secondTry, missed,     // counts
 *              wrong: { "<bid>": count },             // every wrong bid made
 *              last: "first" | "second" | "missed",   // latest outcome
 *              lastAt } } }
 *
 * Kept so Paul can point people at the questions they need extra help with
 * (scripts/_guided-bidding-report.js). A signed-out player's results stay in this
 * browser only (localStorage), so the "needs another look" marks still work for them.
 *
 * Rules (firestore.rules): a user reads/writes only their own document; admins read all.
 */
import { firebase } from "../../firebase/config";

const LS_KEY = "bc.guidedBidding.v1";

export const questionKey = (setId, handId, stepId) => `${setId}|${handId}|${stepId}`;

function readLocal() {
  try {
    return JSON.parse(window.localStorage.getItem(LS_KEY) || "{}") || {};
  } catch (e) {
    return {};
  }
}

function writeLocal(q) {
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(q));
  } catch (e) {
    /* private window / storage blocked - results just aren't kept locally */
  }
}

/** Outcome of one question: wrongBids = the wrong bids made before it ended (0, 1 or 2).
 *  Resolves { outcome, firstTime } - firstTime = first answer ever saved for this question. */
export async function recordQuestion(uid, key, wrongBids) {
  const outcome = wrongBids.length === 0 ? "first" : wrongBids.length === 1 ? "second" : "missed";

  const local = readLocal();
  const prev = local[key] || { seen: 0, firstTry: 0, secondTry: 0, missed: 0, wrong: {} };
  const next = {
    ...prev,
    seen: prev.seen + 1,
    firstTry: prev.firstTry + (outcome === "first" ? 1 : 0),
    secondTry: prev.secondTry + (outcome === "second" ? 1 : 0),
    missed: prev.missed + (outcome === "missed" ? 1 : 0),
    wrong: { ...prev.wrong },
    last: outcome,
    lastAt: Date.now(),
  };
  wrongBids.forEach((b) => {
    next.wrong[b] = (next.wrong[b] || 0) + 1;
  });
  local[key] = next;
  writeLocal(local);

  if (!uid) return { outcome, firstTime: false };
  const inc = firebase.firestore.FieldValue.increment;
  const now = firebase.firestore.FieldValue.serverTimestamp;
  const wrong = {};
  wrongBids.forEach((b) => {
    wrong[b] = inc(1);
  });
  const ref = firebase.firestore().collection("guidedBiddingResults").doc(uid);
  try {
    // firstAt is stamped once, the first time this question is answered (kept for the report;
    // the ladder scores finished HANDS - see recordHandDone).
    let firstTime = false;
    await firebase.firestore().runTransaction(async (tx) => {
      const snap = await tx.get(ref);
      const had = snap.exists && snap.data().q && snap.data().q[key] && snap.data().q[key].firstAt;
      firstTime = !had;
      tx.set(
        ref,
        {
          uid,
          updatedAt: now(),
          q: {
            [key]: {
              seen: inc(1),
              firstTry: inc(outcome === "first" ? 1 : 0),
              secondTry: inc(outcome === "second" ? 1 : 0),
              missed: inc(outcome === "missed" ? 1 : 0),
              wrong,
              last: outcome,
              lastAt: now(),
              ...(firstTime ? { firstAt: now() } : {}),
            },
          },
        },
        { merge: true }
      );
    });
    return { outcome, firstTime };
  } catch (e) {
    // Never block the trainer on a failed write; the local copy is still kept.
    // eslint-disable-next-line no-console
    console.warn("guided bidding: result not saved", e && e.message);
  }
  return { outcome, firstTime: false };
}

/**
 * A finished hand: q["<setId>|<handId>|done"] = { firstAt, lastAt, seen }. firstAt is stamped once -
 * the Game Plan ladder scores POINTS.guided per hand by it. Resolves true the first time.
 */
export async function recordHandDone(uid, key) {
  if (!uid) return false;
  const now = firebase.firestore.FieldValue.serverTimestamp;
  const ref = firebase.firestore().collection("guidedBiddingResults").doc(uid);
  try {
    let firstTime = false;
    await firebase.firestore().runTransaction(async (tx) => {
      const snap = await tx.get(ref);
      const had = snap.exists && snap.data().q && snap.data().q[key] && snap.data().q[key].firstAt;
      firstTime = !had;
      tx.set(
        ref,
        {
          uid,
          updatedAt: now(),
          q: { [key]: { seen: firebase.firestore.FieldValue.increment(1), lastAt: now(), ...(firstTime ? { firstAt: now() } : {}) } },
        },
        { merge: true }
      );
    });
    return firstTime;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("guided bidding: finished hand not saved", e && e.message);
    return false;
  }
}

/** { key: {last, ...} } for this user - Firestore when signed in, merged over the local copy. */
export async function loadResults(uid) {
  const local = readLocal();
  if (!uid) return local;
  try {
    const snap = await firebase.firestore().collection("guidedBiddingResults").doc(uid).get();
    const q = (snap.exists && snap.data().q) || {};
    return { ...local, ...q };
  } catch (e) {
    return local;
  }
}

/**
 * Per-account grant for one set (guidedBiddingGrants/<uid>, written by scripts/_grant-guided-set.js):
 * { sets: { "<setId>": { expires: Timestamp } } }. Returns the set ids whose grant is still current.
 * Paul, 30 Oct 2026: "I only want nick able to view that particular set, not every set".
 */
export async function loadSetGrants(uid) {
  if (!uid) return [];
  try {
    const snap = await firebase.firestore().collection("guidedBiddingGrants").doc(uid).get();
    const sets = (snap.exists && snap.data().sets) || {};
    const now = Date.now();
    return Object.keys(sets).filter((id) => {
      const e = sets[id] && sets[id].expires;
      const ms = e && e.toMillis ? e.toMillis() : e && e.seconds ? e.seconds * 1000 : 0;
      return ms > now;
    });
  } catch (e) {
    return [];
  }
}
